// hooks/useFighter.js
import { useQuery, useQueryClient } from "react-query";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";

const fetchFighter = async (fighterId, getAccessTokenSilently) => {
  const token = await getAccessTokenSilently();
  const apiUrl = process.env.REACT_APP_API_URL;

  const { data } = await axios.get(`${apiUrl}/api/fighters/${fighterId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  return data;
};

/**
 * Hook: useFighter
 * Fetches a fighter by FighterId from your API (MongoDB-backed).
 *
 * @param {number|string} fighterId
 * @param {object} options react-query options override (optional)
 */
export const useFighter = (fighterId, options = {}) => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery(
    ["fighter", fighterId],
    () => fetchFighter(fighterId, getAccessTokenSilently),
    {
      enabled: !!fighterId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      ...options,
    }
  );
};

/**
 * Helper: usePrefetchFighter
 * Returns a function you can call (e.g., onMouseEnter for an info icon)
 * to warm the cache for a given fighterId.
 *
 * Usage:
 *   const prefetch = usePrefetchFighter();
 *   <button onMouseEnter={() => prefetch(fighterId)} … />
 */
export const usePrefetchFighter = () => {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  return (fighterId) => {
    if (!fighterId) return;
    return queryClient.prefetchQuery(
      ["fighter", fighterId],
      () => fetchFighter(fighterId, getAccessTokenSilently),
      {
        // Keep prefetched data warm for a bit so the modal opens instantly
        staleTime: 5 * 60 * 1000, // 5 minutes
      }
    );
  };
};
